import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Bid, LotBidState } from '../types/bid.types';
import { BidIncrementBand } from '../types/auction.types';
import { calculateNextMinBid } from '../services/bidding.service';

export const useLotBids = (
  auctionId: string,
  lotId: string,
  currentUserId?: string,
  bidIncrements?: BidIncrementBand[]
) => {
  const [bidState, setBidState] = useState<LotBidState>({
    lotId,
    currentBid: 0,
    bidCount: 0,
    bids: [],
    amIWinning: false,
    nextMinBid: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!auctionId || !lotId) {
      setLoading(false);
      return;
    }

    // Set up real-time listener for bids
    const bidsQuery = query(
      collection(db, 'bids'),
      where('auctionId', '==', auctionId),
      where('lotId', '==', lotId),
      orderBy('placedAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      bidsQuery,
      (snapshot) => {
        // 'pending' bids are in flight (not yet granted by the server) — they are not part of
        // the lot's bid state: not the high bid, not "my bids", not in the count or history.
        const bids: Bid[] = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          placedAt: doc.data().placedAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate(),
        } as Bid)).filter(b => b.status !== 'pending');

        // Find current winning bid
        const winningBid = bids.find(b => b.isWinning);
        const currentBid = winningBid?.amount || 0;
        const currentBidder = winningBid?.bidderId;

        // Find user's bids
        const myBids = currentUserId
          ? bids.filter(b => b.bidderId === currentUserId)
          : [];
        const myHighestBid = myBids.length > 0 ? myBids[0] : undefined;
        const amIWinning = currentUserId ? currentBidder === currentUserId : false;

        setBidState({
          lotId,
          currentBid,
          currentBidder,
          bidCount: bids.length,
          bids,
          myHighestBid,
          amIWinning,
          nextMinBid: calculateNextMinBid(currentBid, bidIncrements),
        });

        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Bid listener error:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [auctionId, lotId, currentUserId, bidIncrements]);

  return { bidState, loading, error };
};